import { createSelector } from '@reduxjs/toolkit'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { retrieveCommonCares } from '../../core/use-cases/retrieveCommonCares'
import { selectCommonCares } from '../../redux/reducers/commonCares.reducer'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import { Select } from '../ui/form/Select'
import * as S from '../styles'
import styled from '@emotion/styled'
import { mq } from '../breakpoints'
import { selectType } from '../../redux/reducers/simulation.reducer'

export type CommonCareFormProps = {
  onSelectionChange: (categoryLabel: string, label: string) => void
  disabled: boolean
}

const selectCategoryLabelsFromCommonCares = createSelector(
  selectCommonCares,
  state =>
    Array.from(new Set(state.value.map(care => care.categoryLabel))).sort()
)

const selectCommonCaresLoading = createSelector(
  selectCommonCares,
  state => state.loading
)

const selectCommonCaresLabelByCategory = (categoryLabel: string) =>
  createSelector(selectCommonCares, state =>
    Array.from(
      new Set(
        state.value
          .filter(care => care.categoryLabel === categoryLabel)
          .map(care => care.label)
      )
    ).sort()
  )

export const CommonCareForm = ({
  onSelectionChange,
  disabled,
}: CommonCareFormProps) => {
  const dispatch = useAppDispatch()
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState('')
  const [selectedLabel, setSelectedLabel] = useState('')
  const categoryLabels = useSelector(selectCategoryLabelsFromCommonCares)
  const commonCaresLoading = useSelector(selectCommonCaresLoading)
  const commonLabels = useSelector(
    selectCommonCaresLabelByCategory(selectedCategoryLabel)
  )
  const simulationType = useAppSelector(selectType)

  useEffect(() => {
    dispatch(retrieveCommonCares())
  }, [])

  useEffect(() => {
    setSelectedCategoryLabel('')
    setSelectedLabel('')
  }, [simulationType])

  return (
    <Container disabled={disabled}>
      <S.FormContainer>
        <S.InputGroup>
          <S.InputLabel>Catégorie de soin</S.InputLabel>
          <Select
            placeholder=''
            isDisabled={disabled}
            isLoading={commonCaresLoading}
            value={{
              label: selectedCategoryLabel,
              value: selectedCategoryLabel,
            }}
            options={categoryLabels.map(label => ({
              value: label,
              label,
            }))}
            onChange={option => {
              setSelectedCategoryLabel(option.value)
              onSelectionChange(option.value, selectedLabel)
            }}
          />
        </S.InputGroup>
        <S.InputGroup>
          <S.InputLabel>Nom du soin</S.InputLabel>
          <Select
            isDisabled={disabled}
            placeholder='Votre soin courant'
            isLoading={false}
            value={{
              label: selectedLabel,
              value: selectedLabel,
            }}
            options={commonLabels.map(label => ({
              value: label,
              label: label,
            }))}
            onChange={option => {
              setSelectedLabel(option.value)
              onSelectionChange(selectedCategoryLabel, option.value)
            }}
          />
        </S.InputGroup>
      </S.FormContainer>
    </Container>
  )
}

const Container = styled.div<{ disabled: boolean }>`
  flex: 1;
  display: ${props => (props.disabled ? 'none' : 'block')};

  ${mq.lg} {
    display: block;
    opacity: ${props => (props.disabled ? '0.5' : '1')};
  }
`
